import React, { useMemo, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import styles from './AdditionalExerciseModal.module.scss';
import Modal from '@src/components/modal/Modal';
import TicketIcon from 'src/assets/icons/content_progress_feedback.svg?react';
import ConnectIcon from 'src/assets/icons/arrow_connect.svg?react';
import MarkCheckIcon from 'src/assets/icons/mark_check.svg?react';
import Button from '@src/components/button/Button';
import { Lesson, LessonStatus } from '@src/types';
import { SectionFragment, SectionFragmentDoc } from '@src/api/graphql-codegen';

type Props = {
  open: boolean;
  onClose: () => void;
  lesson: Lesson | undefined;
  onStartIntroduction: (exercise: SectionFragment) => void;
  onStartExercise: (exercise: SectionFragment) => void;
};

// Alternate Lesson Exercise
function AdditionalExerciseModal(props: Props) {
  const { open, lesson, onClose, onStartIntroduction, onStartExercise } = props;
  const client = useApolloClient();
  const exercises = useMemo(() => ([...(lesson?.sections || [])].filter(section => section.name.toLowerCase() !== "introduction" && !!section.modules?.length).map(sec => {
    if (sec.id && client) {
      const sectionInCache = client.readFragment({
        id: `Section:${sec.id}`,
        fragment: SectionFragmentDoc,
        fragmentName: 'Section'
      });
      return sectionInCache || sec;
    }
    return sec;
  })), [client, lesson?.sections]);

  const introduction = useMemo(() => {
    const introSection = [...(lesson?.sections || [])].find(section => section.name.toLowerCase() === "introduction");
    if (introSection?.id) {
      const sectionInCache = client.readFragment({
        id: `Section:${introSection?.id}`,
        fragment: SectionFragmentDoc,
        fragmentName: 'Section'
      });
      return sectionInCache || introSection;
    }
  }, [client, lesson?.sections]);

  const handleStartExercise = useCallback((exercise: SectionFragment) => {
    onStartExercise(exercise)
  }, [onStartExercise]);

  const handleStartIntroduction = useCallback(() => {
    if (introduction) {
      onStartIntroduction(introduction)
    }
  }, [introduction, onStartIntroduction]);

  const introductionCompleted = useMemo(() => introduction ? introduction?.status === LessonStatus.Completed : true, [introduction]);

  return (
    <Modal global={false} open={open} onClose={onClose} className={styles.wrapper} closeButton={false} closeOnOverlayClick={false}>
      <div className={styles.container}>
        <div className={styles.title}>Choose your Exercise:</div>
        <div className={styles.content}>
          {introduction ? (
            <div className={`${styles.introduction} ${introductionCompleted ? styles.completedIntro : styles.pulse}`} onClick={handleStartIntroduction}>
              <span>Introduction</span>
              {introductionCompleted && <div className={styles.markCompleteIntro}>
                <MarkCheckIcon width={42} height={42}/>
              </div>}
            </div>
          ) : null}
          <div className={styles.exerciseView}>
            {introduction ? (
              <>
                <div className={`${styles.connectView} ${introductionCompleted && styles.completeConnectPath}`} onClick={e => e.stopPropagation()}>
                  <ConnectIcon height={39}/>
                </div>
                <div className={styles.preRequestView} onClick={e => e.stopPropagation()}>
                  <div className={styles.preRequestSubView}>Pre-requisite:&nbsp;
                    <div className={styles.preRequestSubtitle}>Introduction</div>
                  </div>
                </div>
              </>
            ): null}
            {exercises.map((exercise, index) => {
                const modules = exercise.modules
                const isCompleted = exercise.status === LessonStatus.Completed;
                return (
                  <Button
                    key={index} size='small' color='orange' label={exercise.name}
                    rightIcon={isCompleted ? <div className={styles.markCompleteExercise}><MarkCheckIcon width={42} height={42}/></div> : undefined}
                    onClick={()=>{handleStartExercise(exercise)}}
                    className={`${!introductionCompleted ? styles.exerciseDisableButton : styles.exerciseButton} ${isCompleted ? styles.completedExercise : ''}`}
                    disabled={!introductionCompleted || modules?.length === 0}
                    leftIcon={exercise.name.toLowerCase() === "exit ticket" ? <TicketIcon /> : undefined}
                  />
                )
              })
            }
          </div>
        </div>
        <div className={styles.action}>
          <Button size="small" className={styles.cancelButton} onClick={onClose} label={'Return to Unit'}/>
        </div>
      </div>
    </Modal>
  );
}

export default AdditionalExerciseModal;
